import React, { useRef, useEffect } from 'react'
import { Checkbox, TableCell, TableHead, TableRow } from '@mui/material'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import './heavyTable.css'
// Compact uppercase header (phase-2 design). Kept inline as well as in
// heavyTable.css so every table using this head gets the same look.
const tableHeadStyle = {
    padding: '11px 18px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--color-text-tertiary)', cursor: 'pointer', whiteSpace: 'nowrap',
    backgroundColor: 'var(--color-surface-subtle)', backgroundClip: 'padding-box'
}
const fixedHeaderStyle = (widths, id, isCheatingTable, stickyOffset) => {
    let index = widths.findIndex(w => w.id === id)
    let w = widths[index - 1]?.totalWidth || 0
    return { ...tableHeadStyle, position: 'sticky', zIndex: 999, left: (index == 2 && isCheatingTable ? w - 90 : w) + stickyOffset }
}
const EachHead = ({ header, handleRequestSort, widths, setWidths, fixed, isCheatingTable, stickyOffset }) => {
    const { id, label } = header
    const ref = useRef(null)
    useEffect(() => {
        let newWidths = [...widths]
        let index = newWidths.findIndex(n => n.id === id)
        let prev = newWidths[index - 1]?.totalWidth || 0
        if(index!=-1){
       newWidths[index].width = ref.current?.offsetWidth
        newWidths[index].totalWidth = prev + ref.current?.offsetWidth}
        setWidths(newWidths)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const togglestyles = (label)=>{
        // Label markers used by the integrity-check table keep their colours
        let firstchar = label.charAt(0)
        if(firstchar==='&') return { ...tableHeadStyle, color: 'var(--color-primary)' }
        if(firstchar==='#') return { ...tableHeadStyle, color: 'var(--color-danger)' }
        if(firstchar==='%') return { ...tableHeadStyle, color: 'var(--color-text)' }
        return tableHeadStyle
    }
    const isFixed = fixed.includes(id)
    const isLastFixed = isFixed && fixed[fixed.length - 1] === id
    return (
        <TableCell
            className={isFixed ? `simcc-pinned${isLastFixed ? ' simcc-pinned--edge' : ''}` : undefined}
            style={isFixed ? fixedHeaderStyle(widths, id, isCheatingTable, stickyOffset) : togglestyles(label)}
            onClick={() => handleRequestSort(id)} ref={ref} data-head-id={id}>
            <span className='simcc-th'>
                {label}
                <span className='simcc-th__sort' aria-hidden='true'>
                    <ArrowDropUpIcon />
                    <ArrowDropDownIcon />
                </span>
            </span>
        </TableCell>
    )
}
export default function EnhancedTableHead({ handleRequestSort, headers, widths, setWidths, fixed, isCheatingTable, selectAll, stickyOffset = 0 }) {
    // Pinned columns are offset by the widths of the columns before them. Those
    // widths were only measured on mount, so new data (e.g. after filtering)
    // left pinned columns at stale offsets. Re-measure whenever a header resizes.
    const rowRef = useRef(null)
    useEffect(() => {
        const row = rowRef.current
        if (!row || typeof ResizeObserver === 'undefined') return undefined
        let frame
        const measure = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                let total = 0
                const next = headers.map(h => {
                    const cell = row.querySelector(`[data-head-id="${CSS.escape(String(h.id))}"]`)
                    const width = cell ? cell.offsetWidth : 0
                    total += width
                    return { id: h.id, width, totalWidth: total }
                })
                setWidths(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
            })
        }
        const observer = new ResizeObserver(measure)
        row.querySelectorAll('[data-head-id]').forEach(cell => observer.observe(cell))
        return () => {
            cancelAnimationFrame(frame)
            observer.disconnect()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers])
    return (
        <TableHead>
            <TableRow ref={rowRef}>
                {selectAll && (
                    <TableCell className='simcc-heavy-table__check simcc-pinned' style={{ ...tableHeadStyle, padding: '0 0 0 6px', width: 48, cursor: 'default', position: 'sticky', left: 0, zIndex: 999 }}>
                        <Checkbox
                            size='small'
                            inputProps={{ 'aria-label': 'Select all rows' }}
                            indeterminate={selectAll.indeterminate}
                            checked={selectAll.checked}
                            onChange={selectAll.onChange}
                        />
                    </TableCell>
                )}
                {headers.map(header => (
                    <EachHead key={header.id} id={header.id} header={header} handleRequestSort={handleRequestSort}
                        widths={widths} setWidths={setWidths} fixed={fixed} isCheatingTable={isCheatingTable}
                        stickyOffset={stickyOffset} />
                ))}
            </TableRow>
        </TableHead>
    )
}
